import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { photoApi } from '@/lib/api/photo.api'
import { createLogger } from '@/lib/logger'
import { profileImageCache } from '@/lib/services/profile-image-cache.service'
import { cn } from '@/lib/utils'
import { useAuth } from '@/store/auth.store'
import { CircleNotchIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { invalidateUserAvatarImage } from './user-avatar'

const logger = createLogger('components/ui/avatar-with-upload')

interface User {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
  displayName: string | null
}

interface AvatarWithUploadProps {
  user?: User | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  showUpload?: boolean
  uploadMode?: 'immediate' | 'deferred'
  previewUrl?: string | null
  onFileSelected?: (file: File, previewUrl: string) => void
  onUploadSuccess?: () => void
  onUploadStart?: () => void
  onUploadError?: (error: Error) => void
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
  '2xl': 'h-32 w-32 text-xl',
}

export function AvatarWithUpload({
  user,
  size = 'md',
  className,
  showUpload = false,
  uploadMode = 'immediate',
  previewUrl,
  onFileSelected,
  onUploadSuccess,
  onUploadStart,
  onUploadError,
}: AvatarWithUploadProps) {
  const { user: currentUser } = useAuth()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Track the current user ID to prevent stale updates
  const currentUserIdRef = useRef<string | null>(null)

  const targetUser = user || currentUser
  const canUpload = showUpload && targetUser?.id === currentUser?.id

  // User initials (React Compiler handles memoization)
  const initials = (() => {
    if (!targetUser) return 'U'

    const first = targetUser.firstName?.[0]?.toUpperCase()
    const last = targetUser.lastName?.[0]?.toUpperCase()
    if (first && last) return `${first}${last}`
    if (first) return first

    const displayInitial = targetUser.displayName?.[0]?.toUpperCase()
    const emailInitial = targetUser.email?.[0]?.toUpperCase()

    return displayInitial || emailInitial || 'U'
  })()

  const loadProfileImage = useCallback(
    async (userId: string, mounted: () => boolean) => {
      try {
        const url = await profileImageCache.getProfileImage(userId)
        if (mounted() && currentUserIdRef.current === userId) {
          setImageUrl(url)
          setIsLoading(false)
        }
      } catch (error) {
        logger.error('Error loading profile image:', error)
        if (mounted() && currentUserIdRef.current === userId) {
          setImageUrl(null)
          setIsLoading(false)
        }
      }
    },
    [],
  )

  // Improved profile image loading with anti-flashing logic
  useEffect(() => {
    if (previewUrl) {
      setImageUrl(previewUrl)
      setIsLoading(false)
      return
    }

    if (!targetUser?.id) {
      setImageUrl(null)
      setIsLoading(false)
      currentUserIdRef.current = null
      return
    }

    const userId = targetUser.id
    currentUserIdRef.current = userId

    let mounted = true

    // Only show loading state for truly initial loads
    if (!imageUrl) {
      setIsLoading(true)
    }

    void loadProfileImage(userId, () => mounted)

    return () => {
      mounted = false
    }
  }, [targetUser?.id, previewUrl, loadProfileImage, imageUrl])

  useEffect(() => {
    if (!targetUser?.id || typeof window === 'undefined') {
      return
    }

    const handleAvatarUpdate = (event: Event) => {
      const updatedUserId = (event as CustomEvent<{ userId?: string }>).detail
        ?.userId
      if (updatedUserId !== targetUser.id) {
        return
      }

      void loadProfileImage(updatedUserId, () => true)
    }

    window.addEventListener('scrambl:user-avatar-updated', handleAvatarUpdate)

    return () => {
      window.removeEventListener(
        'scrambl:user-avatar-updated',
        handleAvatarUpdate,
      )
    }
  }, [targetUser?.id, loadProfileImage])

  // Auto-crop image to square (center crop)
  const autoCropToSquare = (
    img: HTMLImageElement,
    maxSize: number = 800,
  ): HTMLCanvasElement => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!

    // Get the smallest dimension for a square crop
    const size = Math.min(img.width, img.height)

    // Calculate center crop coordinates
    const sx = (img.width - size) / 2
    const sy = (img.height - size) / 2

    // Set canvas size (limit to maxSize)
    const outputSize = Math.min(size, maxSize)
    canvas.width = outputSize
    canvas.height = outputSize

    // Draw the center-cropped square
    ctx.drawImage(img, sx, sy, size, size, 0, 0, outputSize, outputSize)

    return canvas
  }

  // Handle file selection - auto-crop and upload immediately
  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !targetUser?.id) return

    e.target.value = ''

    try {
      setIsUploading(true)
      onUploadStart?.()

      // Load image
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = URL.createObjectURL(file)
      })

      // Auto-crop to square
      const croppedCanvas = autoCropToSquare(img, 800)

      // Clean up the object URL
      URL.revokeObjectURL(img.src)

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        croppedCanvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('Failed to create blob'))),
          'image/jpeg',
          0.8,
        )
      })

      const uploadFile = new File([blob], `profile-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      })

      if (uploadMode === 'deferred') {
        const localUrl = URL.createObjectURL(uploadFile)
        setImageUrl(localUrl)
        setIsUploading(false)
        onFileSelected?.(uploadFile, localUrl)
        return
      }

      // Upload in background
      photoApi
        .uploadAndUpdateProfileImage(uploadFile)
        .then(async () => {
          profileImageCache.invalidateUsers([targetUser.id])
          // Try to refresh the image
          try {
            const newBlobUrl = await photoApi.getProfileImageBlob(targetUser.id)
            setImageUrl(newBlobUrl)
          } catch (refreshError) {
            logger.warn('Error refreshing image after upload:', refreshError)
          }
          invalidateUserAvatarImage(targetUser.id)
          setIsUploading(false)
          onUploadSuccess?.()
        })
        .catch(error => {
          logger.error('Error uploading image:', error)
          toast.error(
            error instanceof Error ? error.message : 'Failed to upload image',
          )
          setIsUploading(false)
          onUploadError?.(
            error instanceof Error
              ? error
              : new Error('Failed to upload image'),
          )
        })
    } catch (error) {
      logger.error('Error processing image:', error)
      toast.error('Error processing image. Please try again.')
      setIsUploading(false)
      onUploadError?.(
        error instanceof Error ? error : new Error('Error processing image'),
      )
    }
  }

  const handleUploadClick = () => {
    if (isUploading) return // Prevent clicking while uploading
    fileInputRef.current?.click()
  }

  return (
    <>
      {/* The size lives on the wrapper and the avatar fills it, so a caller's
          own size (`h-16 w-16`) moves the photo, the hover overlay and the
          camera badge together. Sizing only the avatar left a 48px photo in
          the corner of a 64px overlay circle. */}
      <div className={cn('group relative', sizeClasses[size], className)}>
        <Avatar className="h-full w-full">
          {/* Fallback: white initials on the identifier's own color, like
              every other user avatar. The brand `text-primary` used to leak
              in here, and the client portal remaps that to its accent, so
              the initials could land on a near-identical hue. */}
          {!isLoading && imageUrl ? (
            <AvatarImage
              src={imageUrl}
              alt={targetUser?.displayName || targetUser?.firstName || 'User'}
            />
          ) : (
            <AvatarFallback
              className="font-semibold text-white"
              identifier={targetUser?.id || targetUser?.email || ''}
            >
              {initials}
            </AvatarFallback>
          )}
        </Avatar>

        {canUpload && (
          <div
            className={cn(
              'absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/30 transition-opacity',
              isUploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
            onClick={handleUploadClick}
          >
            {isUploading ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin text-white" />
            ) : (
              <PencilSimpleIcon className="h-4 w-4 text-white" />
            )}
          </div>
        )}
      </div>

      {canUpload && (
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileSelect}
        />
      )}
    </>
  )
}
